/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { ClosedEnum } from "../../types/enums.js";
import {
  RiskDetectionScope,
  RiskDetectionScope$Outbound,
  RiskDetectionScope$outboundSchema,
} from "./riskdetectionscope.js";
import {
  RiskMCPScope,
  RiskMCPScope$Outbound,
  RiskMCPScope$outboundSchema,
} from "./riskmcpscope.js";
import {
  RiskPolicyApprovalConfig,
  RiskPolicyApprovalConfig$Outbound,
  RiskPolicyApprovalConfig$outboundSchema,
} from "./riskpolicyapprovalconfig.js";
import {
  RiskPolicyModelConfig,
  RiskPolicyModelConfig$Outbound,
  RiskPolicyModelConfig$outboundSchema,
} from "./riskpolicymodelconfig.js";

/**
 * Policy action: flag, warn (challenge), block, quarantine (deny and freeze the hook session), or require_approval (hold the call until a listed approver approves it). MCP-scoped policies support flag, block and require_approval.
 */
export const CreateRiskPolicyRequestBodyAction = {
  Flag: "flag",
  Warn: "warn",
  Block: "block",
  Quarantine: "quarantine",
  RequireApproval: "require_approval",
} as const;
/**
 * Policy action: flag, warn (challenge), block, quarantine (deny and freeze the hook session), or require_approval (hold the call until a listed approver approves it). MCP-scoped policies support flag, block and require_approval.
 */
export type CreateRiskPolicyRequestBodyAction = ClosedEnum<
  typeof CreateRiskPolicyRequestBodyAction
>;

/**
 * Policy audience type: everyone or targeted.
 */
export const AudienceType = {
  Everyone: "everyone",
  Targeted: "targeted",
} as const;
/**
 * Policy audience type: everyone or targeted.
 */
export type AudienceType = ClosedEnum<typeof AudienceType>;

/**
 * Policy type: standard (regex/presidio/custom detection) or prompt_based (LLM-judge). Defaults to standard.
 */
export const PolicyType = {
  Standard: "standard",
  PromptBased: "prompt_based",
} as const;
/**
 * Policy type: standard (regex/presidio/custom detection) or prompt_based (LLM-judge). Defaults to standard.
 */
export type PolicyType = ClosedEnum<typeof PolicyType>;

/**
 * Default disposition for shadow MCP blocking policies: block_all (default) blocks every non-Speakeasy-hosted server unless allowed, allow_all permits every server unless blocked. Only valid with the shadow_mcp source and block action. Immutable after create — switching requires delete + recreate.
 */
export const ShadowMcpDisposition = {
  BlockAll: "block_all",
  AllowAll: "allow_all",
} as const;
/**
 * Default disposition for shadow MCP blocking policies: block_all (default) blocks every non-Speakeasy-hosted server unless allowed, allow_all permits every server unless blocked. Only valid with the shadow_mcp source and block action. Immutable after create — switching requires delete + recreate.
 */
export type ShadowMcpDisposition = ClosedEnum<typeof ShadowMcpDisposition>;

export type CreateRiskPolicyRequestBody = {
  /**
   * Policy action: flag, warn (challenge), block, quarantine (deny and freeze the hook session), or require_approval (hold the call until a listed approver approves it). MCP-scoped policies support flag, block and require_approval.
   */
  action?: CreateRiskPolicyRequestBodyAction | undefined;
  approvalConfig?: RiskPolicyApprovalConfig | undefined;
  /**
   * For the account_identity source: corporate email domains considered approved. Sessions whose AI-account email domain is not listed are flagged. Empty/omitted leaves the domain rule inert.
   */
  approvedEmailDomains?: Array<string> | undefined;
  /**
   * Principal URNs this policy applies to. For audience_type=everyone, the server stores user:all.
   */
  audiencePrincipalUrns?: Array<string> | undefined;
  /**
   * Policy audience type: everyone or targeted.
   */
  audienceType?: AudienceType | undefined;
  /**
   * Whether the policy name should be auto-generated.
   */
  autoName?: boolean | undefined;
  /**
   * Custom detection rule ids to attach as detectors: a match produces a finding.
   */
  customRuleIds?: Array<string> | undefined;
  /**
   * Per-category detection scopes. Each specified category replaces its centrally recommended scope; a scope with both predicates empty scans every message surface. Empty/omitted = all recommendations apply unchanged.
   */
  detectionScopes?: Array<RiskDetectionScope> | undefined;
  /**
   * Canonical rule_ids the user has unchecked within otherwise-enabled categories. Matching findings are dropped at scan time.
   */
  disabledRules?: Array<string> | undefined;
  /**
   * Whether the policy is active.
   */
  enabled?: boolean | undefined;
  mcpScope?: RiskMCPScope | undefined;
  modelConfig?: RiskPolicyModelConfig | undefined;
  /**
   * The policy name. If omitted, a name will be auto-generated.
   */
  name?: string | undefined;
  /**
   * Policy type: standard (regex/presidio/custom detection) or prompt_based (LLM-judge). Defaults to standard.
   */
  policyType?: PolicyType | undefined;
  /**
   * Presidio entity types to detect.
   */
  presidioEntities?: Array<string> | undefined;
  /**
   * Minimum Presidio confidence (0.0-1.0) a PII match must clear to surface. Omit/null applies the default (0.5).
   */
  presidioScoreThreshold?: number | undefined;
  /**
   * For prompt_based policies: the guardrail prompt the LLM judge evaluates each in-scope message against. Required when policy_type is prompt_based.
   */
  prompt?: string | undefined;
  /**
   * Prompt-injection detection rule ids to enable in addition to the heuristic baseline.
   */
  promptInjectionRules?: Array<string> | undefined;
  /**
   * CVSS-style severity (0.1-10) assigned to findings this policy produces. Omit to apply the default (5).
   */
  score?: number | undefined;
  /**
   * Complete desired canonical URL allow set for this policy. Omit or send empty to create no URL-specific allow decisions.
   */
  shadowMcpAllowedUrls?: Array<string> | undefined;
  /**
   * For allow_all policies: complete desired canonical URL block set. Omit or send empty to block nothing. Only valid when shadow_mcp_disposition is allow_all.
   */
  shadowMcpBlockedUrls?: Array<string> | undefined;
  /**
   * Default disposition for shadow MCP blocking policies: block_all (default) blocks every non-Speakeasy-hosted server unless allowed, allow_all permits every server unless blocked. Only valid with the shadow_mcp source and block action. Immutable after create — switching requires delete + recreate.
   */
  shadowMcpDisposition?: ShadowMcpDisposition | undefined;
  /**
   * Detection sources to enable.
   */
  sources?: Array<string> | undefined;
  /**
   * Optional message shown to end users when this policy blocks an action or surfaces a flagged finding.
   */
  userMessage?: string | undefined;
};

/** @internal */
export const CreateRiskPolicyRequestBodyAction$outboundSchema: z.ZodMiniEnum<
  typeof CreateRiskPolicyRequestBodyAction
> = z.enum(CreateRiskPolicyRequestBodyAction);

/** @internal */
export const AudienceType$outboundSchema: z.ZodMiniEnum<typeof AudienceType> = z
  .enum(AudienceType);

/** @internal */
export const PolicyType$outboundSchema: z.ZodMiniEnum<typeof PolicyType> = z
  .enum(PolicyType);

/** @internal */
export const ShadowMcpDisposition$outboundSchema: z.ZodMiniEnum<
  typeof ShadowMcpDisposition
> = z.enum(ShadowMcpDisposition);

/** @internal */
export type CreateRiskPolicyRequestBody$Outbound = {
  action: string;
  approval_config?: RiskPolicyApprovalConfig$Outbound | undefined;
  approved_email_domains?: Array<string> | undefined;
  audience_principal_urns?: Array<string> | undefined;
  audience_type: string;
  auto_name?: boolean | undefined;
  custom_rule_ids?: Array<string> | undefined;
  detection_scopes?: Array<RiskDetectionScope$Outbound> | undefined;
  disabled_rules?: Array<string> | undefined;
  enabled?: boolean | undefined;
  mcp_scope?: RiskMCPScope$Outbound | undefined;
  model_config?: RiskPolicyModelConfig$Outbound | undefined;
  name?: string | undefined;
  policy_type: string;
  presidio_entities?: Array<string> | undefined;
  presidio_score_threshold?: number | undefined;
  prompt?: string | undefined;
  prompt_injection_rules?: Array<string> | undefined;
  score: number;
  shadow_mcp_allowed_urls?: Array<string> | undefined;
  shadow_mcp_blocked_urls?: Array<string> | undefined;
  shadow_mcp_disposition?: string | undefined;
  sources?: Array<string> | undefined;
  user_message?: string | undefined;
};

/** @internal */
export const CreateRiskPolicyRequestBody$outboundSchema: z.ZodMiniType<
  CreateRiskPolicyRequestBody$Outbound,
  CreateRiskPolicyRequestBody
> = z.pipe(
  z.object({
    action: z._default(
      CreateRiskPolicyRequestBodyAction$outboundSchema,
      "flag",
    ),
    approvalConfig: z.optional(RiskPolicyApprovalConfig$outboundSchema),
    approvedEmailDomains: z.optional(z.array(z.string())),
    audiencePrincipalUrns: z.optional(z.array(z.string())),
    audienceType: z._default(AudienceType$outboundSchema, "everyone"),
    autoName: z.optional(z.boolean()),
    customRuleIds: z.optional(z.array(z.string())),
    detectionScopes: z.optional(z.array(RiskDetectionScope$outboundSchema)),
    disabledRules: z.optional(z.array(z.string())),
    enabled: z.optional(z.boolean()),
    mcpScope: z.optional(RiskMCPScope$outboundSchema),
    modelConfig: z.optional(RiskPolicyModelConfig$outboundSchema),
    name: z.optional(z.string()),
    policyType: z._default(PolicyType$outboundSchema, "standard"),
    presidioEntities: z.optional(z.array(z.string())),
    presidioScoreThreshold: z.optional(z.number()),
    prompt: z.optional(z.string()),
    promptInjectionRules: z.optional(z.array(z.string())),
    score: z._default(z.number(), 5),
    shadowMcpAllowedUrls: z.optional(z.array(z.string())),
    shadowMcpBlockedUrls: z.optional(z.array(z.string())),
    shadowMcpDisposition: z.optional(ShadowMcpDisposition$outboundSchema),
    sources: z.optional(z.array(z.string())),
    userMessage: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      approvalConfig: "approval_config",
      approvedEmailDomains: "approved_email_domains",
      audiencePrincipalUrns: "audience_principal_urns",
      audienceType: "audience_type",
      autoName: "auto_name",
      customRuleIds: "custom_rule_ids",
      detectionScopes: "detection_scopes",
      disabledRules: "disabled_rules",
      mcpScope: "mcp_scope",
      modelConfig: "model_config",
      policyType: "policy_type",
      presidioEntities: "presidio_entities",
      presidioScoreThreshold: "presidio_score_threshold",
      promptInjectionRules: "prompt_injection_rules",
      shadowMcpAllowedUrls: "shadow_mcp_allowed_urls",
      shadowMcpBlockedUrls: "shadow_mcp_blocked_urls",
      shadowMcpDisposition: "shadow_mcp_disposition",
      userMessage: "user_message",
    });
  }),
);

export function createRiskPolicyRequestBodyToJSON(
  createRiskPolicyRequestBody: CreateRiskPolicyRequestBody,
): string {
  return JSON.stringify(
    CreateRiskPolicyRequestBody$outboundSchema.parse(
      createRiskPolicyRequestBody,
    ),
  );
}
